/**
 * The install token as a FILE (release 8, laptop agent deployed once).
 *
 * The customer's own IT administrator mints the token in their own console and
 * downloads it as two files: `bootstrap.token` (exactly the token's characters,
 * no line break, no byte order mark) and `bootstrap.token.sha256` (its SHA-256
 * in sha256sum format). The BigFix wrapper and the installers read the token
 * only from the file and check it against the checksum before anything is
 * installed (apps/edge-discovery/packaging/windows-msi/bigfix/README.md). So
 * the token never travels by e-mail, chat or a pasted document.
 *
 * The checksum is the same value the controller stores for the token
 * (sha256(token), service.ts mintInstallToken). The mint response carries it as
 * bootstrapTokenSha256 and the page checks its own file against it, so the file
 * a person downloads is proven to be the token the controller knows.
 */

export const TOKEN_FILE_NAME = 'bootstrap.token';
export const TOKEN_SUM_FILE_NAME = 'bootstrap.token.sha256';

/** The exact bytes of bootstrap.token: the token, ASCII, nothing added. */
export function tokenFileBytes(token: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(token);
}

/** Lower-case hex SHA-256 of the given bytes, through Web Crypto. */
export async function sha256Hex(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('this browser cannot compute SHA-256 here (the console must be opened over https)');
  const digest = await subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** bootstrap.token.sha256: sha256sum's text format, one line. */
export function checksumFileText(hashHex: string): string {
  return `${hashHex.toLowerCase()}  ${TOKEN_FILE_NAME}\n`;
}

export interface TokenFiles {
  /** The token file's bytes. */
  token: Uint8Array<ArrayBuffer>;
  /** The checksum file's text. */
  checksum: string;
  /** The SHA-256, for the page to show beside the buttons. */
  sha256: string;
  /**
   * True when the browser could not compute SHA-256 itself (Web Crypto is
   * only there over https or on localhost) and the checksum file carries the
   * controller's value unchecked by the page. The installer still checks the
   * token against it, so a mismatch is still refused on the machine.
   */
  unchecked?: boolean;
}

/**
 * Both files for a freshly minted token. When the controller sent its own
 * checksum, the file's must equal it; a difference means the browser would
 * hand out something other than the minted token, so it is refused.
 */
export async function buildTokenFiles(token: string, controllerSha256?: string | null): Promise<TokenFiles> {
  const bytes = tokenFileBytes(token);
  // A console opened over plain http (an intranet address) has no Web Crypto.
  // The token is already minted and this page is the only place to download
  // it, so do not strand the operator: use the controller's checksum, say so.
  if (!globalThis.crypto?.subtle && controllerSha256 && /^[0-9a-f]{64}$/i.test(controllerSha256)) {
    const sha256 = controllerSha256.toLowerCase();
    return { token: bytes, checksum: checksumFileText(sha256), sha256, unchecked: true };
  }
  const sha256 = await sha256Hex(bytes);
  if (controllerSha256 && controllerSha256.toLowerCase() !== sha256) {
    throw new Error('the token file does not match the checksum the controller stored; mint the token again');
  }
  return { token: bytes, checksum: checksumFileText(sha256), sha256 };
}

/** Offer bytes or text to the browser as a file download. */
export function saveFile(name: string, contents: Uint8Array<ArrayBuffer> | string, type = 'application/octet-stream'): void {
  const blob = new Blob([contents], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Some browsers start the download after click() returns; revoking the URL
  // at once can cancel it, so give it a moment.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
